import type { Schemas } from '#shopware'
import { type Ref, computed } from 'vue'
import { useShopwareContext } from '#imports'

export const useDetailPromotion = (detailPromotionData?: Ref<Schemas['ProductPromotionsResponse']['elements'] | null>) => {
    const { apiClient } = useShopwareContext()

    /**
     * Fetches promotion data for a product by ID
     * @param id - Product ID
     * @returns Promise with promotion data or null if ID is empty
     * @throws Error if the request fails
     */
    const fetchPromotionData = async (id: string): Promise<Schemas['ProductPromotionsResponse'] | null> => {
        if (!id) {
            console.warn('fetchPromotionData called with empty ID');
            return null
        }

        try {
            const { data } = await apiClient.invoke('getProductPromotions get /product/{id}/promotion', {
                pathParams: { id }
            })

            return data
        } catch (error) {
            console.error(`Failed to fetch promotion data for product ID: ${id}`, error)
            throw new Error(`Failed to fetch promotion data: ${error instanceof Error ? error.message : 'Unknown error'}`, { cause: error })
        }
    }

    const computedPromotionData = computed(() => {
        const promotionItem = detailPromotionData?.value?.[0]
        if (!promotionItem) return null

        const { extensions, translated } = promotionItem
        const promotion = extensions?.promotion
        const customFields = translated?.customFields

        return {
            id: promotion?.id,
            code: promotion?.code,
            validFrom: promotion?.validFrom,
            validUntil: promotion?.validUntil,
            imageUrl: promotion?.media?.url,
            imageLink: customFields?.product_promotion_image_url,
            title: customFields?.product_promotion_title,
            active: customFields?.product_promotion_active,
            showBadge: customFields?.product_promotion_show_badge,
            multiDeal43: customFields?.product_promotion_badge_multi_deal_43,
            multiDeal43Text: customFields?.product_promotion_multi_deal_text,
        }
    })

    const showPromotion = computed(() => {
        const now = new Date()
        const validFrom = computedPromotionData.value?.validFrom ? new Date(computedPromotionData.value.validFrom) : null
        const validUntil = computedPromotionData.value?.validUntil ? new Date(computedPromotionData.value.validUntil) : null

        // If there's no end date or the end date has passed, don't show
        if (!validUntil || now >= validUntil) return false

        // If there's a start date, and we haven't reached it yet, don't show
        if (validFrom && now < validFrom) return false

        // In all other cases, show the promotion
        return true
    })

    return {
        fetchPromotionData,
        computedPromotionData,
        showPromotion
    }
}
